'use client';

import {
  useCurrentUser,
  useUserResourceSource,
  type VideoProgress,
} from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { InlineSkeleton, IslandKeyframes } from './_shared';

/**
 * Inline "you watched 38%" display for a video. Renders nothing for
 * anonymous users or when the adapter has no progress entry.
 */
export interface WatchProgressProps {
  videoSlug: string;
  format?: 'percent' | 'time';
}

function fmt(seconds: number): string {
  const s = Math.round(seconds);
  const m = Math.floor(s / 60);
  return `${m}:${(s % 60).toString().padStart(2, '0')}`;
}

export function WatchProgress({ videoSlug, format = 'percent' }: WatchProgressProps) {
  const user = useCurrentUser<{ name?: string }>();
  const ur = useUserResourceSource();
  const state = useAsyncResource<VideoProgress>(
    async (signal) => {
      if (!user || !ur?.watchProgress) return null;
      return await ur.watchProgress.fetch(videoSlug, signal);
    },
    [user, ur, videoSlug],
  );

  if (!user) return null;
  if (!ur?.watchProgress) return null;
  if (state.kind === 'loading')
    return (
      <>
        <IslandKeyframes />
        <InlineSkeleton width="80px" />
      </>
    );
  if (state.kind !== 'loaded') return null;

  const p = state.data;
  const display =
    format === 'time'
      ? `${fmt(p.positionSeconds)} / ${fmt(p.durationSeconds)}`
      : `${Math.round(p.progressPercent)}% watched`;
  return (
    <span
      style={{
        fontSize: 'var(--font-size-xs, 12px)',
        color: 'var(--color-text-tertiary, #94867d)',
      }}
    >
      {p.completed ? '✓ Completed' : display}
    </span>
  );
}
